import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";

import { useShiftRouteEstimate } from "../ArrivalTime/ShiftRouteEstimate/useShiftRouteEstimate";
import { useArrivalTimePoints } from "../ArrivalTime/useArrivalTimePoints";
import { useIsArrivalTimeEligible } from "../ArrivalTime/useIsArrivalTimeEligible";
import { useIsDoubleBookedShift } from "./useIsDoubleBookedShift";

interface UseArrivalTimeDataProps {
  workerId: string;
  shift?: {
    id: string;
    attributes: {
      start: string;
    };
  };
  workplace?: {
    id: string;
    attributes: {
      type?: string;
      location: {
        latitude?: number;
        longitude?: number;
        msa?: string;
      };
    };
  };
  isInstantBook?: boolean;
  resolvedShiftQualification?: string;
}

export function useArrivalTimeData(props: UseArrivalTimeDataProps) {
  const { workerId, shift, workplace, isInstantBook, resolvedShiftQualification } = props;

  const isArrivalTimeEligible = useIsArrivalTimeEligible({
    shiftStartIso: shift?.attributes.start,
    isInstantBook,
    workplaceType: workplace?.attributes.type,
    workplaceId: workplace?.id ?? "",
    workplaceMsa: workplace?.attributes?.location?.msa ?? "",
  });

  // Disable showing arrival time if its a double booked shift
  const { isDoubleBookedShift, isLoading: isLoadingDoubleBookedShift } = useIsDoubleBookedShift({
    workerId,
    shift: {
      id: shift?.id ?? "",
      attributes: {
        start: shift?.attributes.start ?? "",
        workplaceId: workplace?.id ?? "",
      },
    },
    enabled: isArrivalTimeEligible && isDefined(shift) && isDefined(workplace?.id),
  });

  useLogEffect(
    APP_V2_APP_EVENTS.ARRIVAL_TIME_UNAVAILABLE,
    {
      reason: "Feature flag is disabled",
    },
    {
      enabled: isArrivalTimeEligible,
    }
  );

  const isRouteEstimateEnabled =
    isDefined(shift) &&
    isDefined(workplace) &&
    isDefined(workerId) &&
    isArrivalTimeEligible &&
    !isDoubleBookedShift;

  const { isLoading: isLoadingRouteEstimate, data: computedArrivalData } = useShiftRouteEstimate(
    {
      destination: {
        latitude: workplace?.attributes.location.latitude ?? 0,
        longitude: workplace?.attributes.location.longitude ?? 0,
      },
      workerId: workerId ?? "",
      shiftId: shift?.id,
      workplaceId: workplace?.id ?? "",
      shiftStartIso: shift?.attributes.start,
    },
    {
      enabled: isRouteEstimateEnabled,
    }
  );
  const isRouteEstimateLoadingAndEnabled = isLoadingRouteEstimate && isRouteEstimateEnabled;

  const { data: arrivalTimePoints, isLoading: isLoadingArrivalTimePoints } = useArrivalTimePoints({
    workerId,
    qualification: resolvedShiftQualification,
  });

  return {
    computedArrivalData,
    arrivalTimePoints,
    isLoadingArrivalTimePoints,
    isRouteEstimateLoadingAndEnabled,
    isLoadingDoubleBookedShift,
  };
}
